/* External */
import { memo, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';

/* Components */
import SwimlaneCarousel from './SwimlaneCarousel';
import SwimlaneLiveSkeleton from '../SwimlaneLiveSkeleton';
import FeaturedCarouselSkeleton from '../FeaturedCarouselSkeleton';
import SwimlaneStationSkeleton from '../SwimlaneStationSkeleton';
import SwimlaneCollectionSkeleton from '../SwimlaneCollectionSkeleton';
import SwimlaneVodSkeleton from '../SwimlaneVodSkeleton';

/* Utils */
import useFetchCarousel from '../../hooks/useFetchCarousel';
import { noOp } from '../../utils/functions';
import {
  CardType, getCardType, getInitialCardType, isEmptyCarousel,
} from '../../utils/cardsUtils';

const ITEMS_PER_CAROUSEL = 20;

function SwimlaneCarouselContainer({
  carouselInfo = undefined,
  avoidFetch = false,
  noFeatured = false,
  noTitle = false,
  pageId = '',
  sourceProvider = '',
  initialPosition = 0,
  initialTranslate = 0,
  stealFocusByDefault = false,
  suppressFeaturedAutoFocus = false,
  scrolled = false,
  onFetchComplete = noOp,
  onNoMoreLeftElements = noOp,
  onPositionCarouselChange = noOp,
  onTranslateChange = noOp,
  onBecameFocused = noOp,
  handleClickSection = noOp,
  carouselVerticalPosition = undefined,
  onSetFocusKey = undefined,
}) {
  const onFetched = useCallback((sourceCarouselId, carousel) => {
    if (onFetchComplete) onFetchComplete(sourceCarouselId, carousel);
  }, [onFetchComplete]);
  const { isFetching, carousel } = useFetchCarousel({
    ...carouselInfo,
    avoidFetch,
    sourceProvider,
    max: ITEMS_PER_CAROUSEL + 1,
    onFetched,
  });

  const initialCardType = useMemo(() => getInitialCardType(
    carouselInfo,
    noFeatured,
  ), [carouselInfo, noFeatured]);

  const cardType = useMemo(() => getCardType(
    carousel,
    noFeatured,
  ), [carousel, noFeatured]);

  const swimlaneSkeleton = useMemo(
    () => {
      switch (initialCardType) {
        case CardType.EMPTY: {
          return undefined;
        }
        case CardType.FEATURED: {
          return <FeaturedCarouselSkeleton contained />;
        }
        case CardType.CHANNEL: {
          return <SwimlaneStationSkeleton />;
        }
        case CardType.COLLECTION: {
          return <SwimlaneCollectionSkeleton />;
        }
        case CardType.VOD: {
          return <SwimlaneVodSkeleton />;
        }
        default: {
          return <SwimlaneLiveSkeleton />;
        }
      }
    },
    [initialCardType],
  );

  if (carousel && (cardType === CardType.EMPTY || isEmptyCarousel(carousel))) {
    // eslint-disable-next-line unicorn/no-null -- React doesn't support undefined as return value
    return null;
  }

  if (isFetching || cardType === CardType.UNKNOWN) {
    // eslint-disable-next-line unicorn/no-null -- React doesn't support undefined as return value
    return swimlaneSkeleton || null;
  }

  return (
    <SwimlaneCarousel
      carousel={carousel}
      scrolled={scrolled}
      cardType={cardType}
      itemsPerCarousel={ITEMS_PER_CAROUSEL}
      stealFocusByDefault={stealFocusByDefault}
      suppressFeaturedAutoFocus={suppressFeaturedAutoFocus}
      initialPosition={initialPosition}
      initialTranslate={initialTranslate}
      onNoMoreLeftElements={onNoMoreLeftElements}
      onPositionCarouselChange={onPositionCarouselChange}
      onTranslateChange={onTranslateChange}
      onBecameFocused={onBecameFocused}
      onSetFocusKey={onSetFocusKey}
      pageId={pageId}
      handleClickSection={handleClickSection}
      sourceProvider={sourceProvider}
      carouselVerticalPosition={carouselVerticalPosition}
      noTitle={noTitle}
    />
  );
}

SwimlaneCarouselContainer.propTypes = {
  carouselInfo: PropTypes.shape({
    sourceCarouselId: PropTypes.string,
    elementId: PropTypes.string,
    source: PropTypes.string,
    type: PropTypes.string,
  }),
  noFeatured: PropTypes.bool,
  noTitle: PropTypes.bool,
  pageId: PropTypes.string,
  sourceProvider: PropTypes.string,
  avoidFetch: PropTypes.bool,
  initialPosition: PropTypes.number,
  initialTranslate: PropTypes.number,
  stealFocusByDefault: PropTypes.bool,
  suppressFeaturedAutoFocus: PropTypes.bool,
  scrolled: PropTypes.bool,
  onFetchComplete: PropTypes.func,
  onNoMoreLeftElements: PropTypes.func,
  onPositionCarouselChange: PropTypes.func,
  onTranslateChange: PropTypes.func,
  onBecameFocused: PropTypes.func,
  onSetFocusKey: PropTypes.func,
  handleClickSection: PropTypes.func,
  carouselVerticalPosition: PropTypes.number,
};

export default memo(SwimlaneCarouselContainer);
